/**
 * Reading an import's answer: which rows were refused and why, in the reader's
 * language.
 *
 * The server sends a reason code per refused row (appointment.Reason in
 * internal/appointment/rows.go) and one detail beside it — the column, the other
 * reference, the line it was first on. The words are here, because the person
 * reading the result may read Azerbaijani or Russian.
 */

import { ApiError } from './api'
import { hasPhrase, t, type StaffKey, type StaffLanguage } from './staffText'
import type { ImportAnswer } from './types'

/** The server's whole-request limit (internal/api/respond.go, maxRequestBody). */
const SERVER_REQUEST_LIMIT = 256 * 1024

/** A cushion below the server's own limit — not an allowance for anything in
 *  particular, since requestBytes already counts exactly what api.ts's
 *  request() sends, but a boundary case costs nothing to leave room for. */
const MARGIN_BYTES = 1024

/** The largest request this screen will offer to send. */
export const MAX_REQUEST_BYTES = SERVER_REQUEST_LIMIT - MARGIN_BYTES

const encoder = new TextEncoder()

/** The request the browser would actually send to import this file, in UTF-8
 *  bytes: what lib/api.ts's request() calls JSON.stringify on for
 *  importAppointments. A file's own bytes undercount this whenever it holds a
 *  quote, a backslash or a newline — each grows once JSON-escaped inside the
 *  "csv" string, so a file that reads as comfortably small can still send a
 *  request the server refuses to finish reading.
 *
 *  dryRun is fixed at false: the Import call's body is the larger of the two
 *  this screen ever sends (one character more than dryRun: true), and Check
 *  must not pass a file that Import would then be refused for. */
export function requestBytes(csv: string): number {
  return encoder.encode(JSON.stringify({ csv, dryRun: false })).length
}

/** The header a file starts with, shown on the screen as the example to copy.
 *  Built from the column names rather than written as one string: they are the
 *  file format's words, the same in every language, and are not staff copy. */
export const SAMPLE_HEADER = ['reference', 'service', 'date', 'time', 'code'].join(';')

export interface RefusedRow {
  line: number
  reference?: string
  reason: string
  arg?: string
}

/** Every refused row — unreadable ones and ones the store refused — by line. */
export function refusedRows(answer: ImportAnswer): RefusedRow[] {
  const refused: RefusedRow[] = [
    ...answer.problems,
    ...answer.rows
      .filter((d) => d.outcome === 'refused')
      .map((d) => ({ line: d.line, reference: d.reference, reason: d.reason ?? '', arg: d.arg })),
  ]
  return refused.sort((a, b) => a.line - b.line)
}

/** Why a row was refused, in words. A reason this screen does not know yet — a
 *  newer server — is said as a plain refusal rather than as its code. */
export function reasonText(row: RefusedRow, lang: StaffLanguage): string {
  const key = `appointments.reason.${row.reason}`
  if (!hasPhrase(key)) return t('appointments.reason.other', lang)
  return t(key as StaffKey, lang, { arg: row.arg ?? '' })
}

/** A single booking's refusal as a refused row, or undefined for any other failure. */
export function bookingRefusal(err: unknown): RefusedRow | undefined {
  if (!(err instanceof ApiError) || (err.code !== 'APPOINTMENT_INVALID' && err.code !== 'APPOINTMENT_REFUSED')) return undefined
  return { line: 0, reason: err.reason ?? '', arg: err.args?.arg }
}

/** Whether the file, as checked, would write anything. */
export function mayImport(answer: ImportAnswer): boolean {
  return answer.created + answer.updated > 0
}

/** Whether importing this file would send a request larger than the server
 *  will read. Takes the file's text, not its size, for the reason
 *  requestBytes does. */
export function tooBig(csv: string): boolean {
  return requestBytes(csv) > MAX_REQUEST_BYTES
}
